import {
  BROADCAST_LANGUAGES,
  BroadcastFilterCombinator,
  BroadcastFilterField,
  BroadcastKycFilterValue,
  BroadcastPlan,
  KYCStatus,
  UserStatus,
} from '@mygate/common';

export type LangCode = 'de' | 'en' | 'ru' | 'pl';

/** Ordered de/en/ru/pl language keys, derived from the shared BROADCAST_LANGUAGES constant. */
export const LANG_KEYS: LangCode[] = BROADCAST_LANGUAGES.map(lang => String(lang) as LangCode);

export const LANGUAGE_LABELS: Record<LangCode, string> = {
  de: 'Deutsch',
  en: 'English',
  ru: 'Русский',
  pl: 'Polski',
};

export const BROADCAST_STATUS_COLORS: Record<string, string> = {
  SENDING: 'processing',
  COMPLETED: 'success',
  FAILED: 'error',
};

// ─── Audience filter labels (MYG-437) ───

export const FILTER_FIELD_LABELS: Record<BroadcastFilterField, string> = {
  userStatus: 'User status',
  kycStatus: 'KYC status',
  hasTradevestAccount: 'Tradevest account',
  tradevestProvisioningStatus: 'Tradevest provisioning status',
  paycenterPlan: 'Plan',
  createdAt: 'Created',
  lastLoginAt: 'Last login',
};

export const FILTER_COMBINATOR_LABELS: Record<BroadcastFilterCombinator, string> = {
  [BroadcastFilterCombinator.AND]: 'All conditions (AND)',
  [BroadcastFilterCombinator.OR]: 'Any condition (OR)',
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: 'Active',
  [UserStatus.SUSPENDED]: 'Suspended',
  [UserStatus.PENDING_VERIFICATION]: 'Pending verification',
  [UserStatus.KYC_PROCESSING]: 'KYC processing',
  [UserStatus.MANUAL_REVIEW]: 'Manual review',
  [UserStatus.DELETION_PENDING]: 'Deletion pending',
};

export const KYC_FILTER_VALUE_LABELS: Record<BroadcastKycFilterValue, string> = {
  [KYCStatus.NEW]: 'New',
  [KYCStatus.REQUIRED]: 'Required',
  [KYCStatus.IN_PROGRESS]: 'In progress',
  [KYCStatus.MANUAL_REVIEW]: 'Manual review',
  [KYCStatus.CLOSED]: 'Closed',
  NONE: 'No KYC case',
};

export const PLAN_LABELS: Record<BroadcastPlan, string> = {
  FREE: 'Free',
  SMART: 'Smart',
};

/** Tradevest provisioning statuses are numerous (snake_case) — humanize instead of hand-maintaining a label map. */
export function humanizeTradevestStatus(status: string): string {
  // The user model defaults this field, so 'not_started' covers users who never touched Tradevest at all —
  // the label must say so or staff will read it as "started but at the initial step" (MYG-437 review F1)
  if (status === 'not_started') {
    return 'Not started (incl. never onboarded)';
  }
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
